import { Location } from 'history';
import humanizeString from 'humanize-string';
import { ReactNode } from 'react';

import { useFavoriteFields } from '.';
import {
  getRecordRenderMap,
  getRecordSchemaVersion,
  recordColumnMap,
  RecordColumnType,
  recordHeaders,
  recordLabels,
  RecordType,
} from '..';
import { parseColumns } from './parsers';
import { METADATA_PREFIX, useVisibleColumnData } from './useVisibleColumnData';

/**
 * The display column interface contains two renderers:
 *   - primaryRenderer - Render function for the first row of the column
 *   - secondaryRenderer - Render function for the second row of the column
 */
interface DisplayColumnType extends RecordColumnType {
  primaryRenderer?: (value: ReactNode, record: RecordType) => ReactNode;
  secondaryRenderer?: (value: ReactNode, record: RecordType) => ReactNode;
}

const getLabelMap = (name: string, location: Location, record: RecordType) => {
  const schemaVersion = getRecordSchemaVersion(record);
  const isRecordSearch = location.pathname === '/sec/records';

  return schemaVersion === 'v3'
    ? !isRecordSearch && recordLabels.hasOwnProperty(name)
      ? recordLabels[name]
      : {
          label: humanizeString(
            name.startsWith(METADATA_PREFIX)
              ? name.replace(METADATA_PREFIX, '')
              : name
          ),
        }
    : getRecordRenderMap()[record._type][name];
};

export const useRecordHeaderColumns = (
  location: Location,
  record: RecordType
) => {
  let displayColumns: DisplayColumnType[] = [];
  const type = record._type;

  const schemaVersion = getRecordSchemaVersion(record);
  const columnMap = schemaVersion === 'v3' ? recordHeaders : recordColumnMap;

  const allColumns = columnMap?.[type] ?? parseColumns(record);

  const allFavoriteFields = useFavoriteFields(record);
  const [visibleColumns, visibleFavoriteFields] = useVisibleColumnData(
    allFavoriteFields,
    allColumns,
    record
  );

  displayColumns = visibleColumns.map((recordColumn) => {
    // Use the renderer if specified.
    const result: DisplayColumnType = {
      primaryLabel: visibleFavoriteFields.length ? '' : 'Info',
    };

    // Get primary value.
    if (recordColumn.primary) {
      const primaryMap = getLabelMap(recordColumn.primary, location, record);
      result.primaryLabel = primaryMap.label;
      result.primaryName = recordColumn.primary;
      if (primaryMap.renderer) {
        result.primaryRenderer = primaryMap.renderer;
      }
    }
    if (recordColumn.renderer) {
      result.primaryRenderer = recordColumn.renderer;
    }

    // Get secondary value.
    if (recordColumn.secondary) {
      const secondaryMap = getLabelMap(
        recordColumn.secondary,
        location,
        record
      );
      result.secondaryLabel = secondaryMap.label;
      result.secondaryName = recordColumn.secondary;

      if (secondaryMap.renderer) {
        result.secondaryRenderer = secondaryMap.renderer;
      }
    }
    if (recordColumn.secondaryRenderer) {
      result.secondaryRenderer = recordColumn.secondaryRenderer;
    }

    return result;
  });

  return displayColumns;
};
